import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { Theme } from '@scoreboard/design-tokens';
import { safeRead, safeWrite } from './i18n';

// Thème CLAIR par défaut (branding/v2/da-v2.md §1), sombre au choix, persisté par
// appareil via `data-theme` sur <html> (doc 05 §9.2 règle 6).
//
// Tant que personne n'a choisi, c'est le SYSTÈME qui décide : `tokens.css` le fait déjà
// en CSS pur (`:root:not([data-theme])` sous `prefers-color-scheme`) pour que la page
// soit au bon thème avant le premier rendu. On relit la même préférence ici, sinon le
// libellé du bouton mentirait sur ce qui est affiché.

const THEME_STORAGE_KEY = 'scorofun.theme';

interface ThemeContextValue {
  theme: Theme;
  toggle: () => void;
}

const ThemeContext = createContext<ThemeContextValue>({ theme: 'light', toggle: () => {} });

function systemTheme(): Theme {
  return globalThis.matchMedia?.('(prefers-color-scheme: dark)').matches === true
    ? 'dark'
    : 'light';
}

export function readStoredTheme(): Theme {
  const stored = safeRead(THEME_STORAGE_KEY);
  if (stored === 'light' || stored === 'dark') {
    return stored;
  }
  return systemTheme();
}

export function ThemeProvider({ children }: { children: React.ReactNode }): React.ReactElement {
  const [theme, setTheme] = useState<Theme>(readStoredTheme);

  useEffect(() => {
    globalThis.document?.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  const toggle = useCallback(() => {
    setTheme((current) => {
      const next: Theme = current === 'dark' ? 'light' : 'dark';
      safeWrite(THEME_STORAGE_KEY, next);
      return next;
    });
  }, []);

  const value = useMemo(() => ({ theme, toggle }), [theme, toggle]);
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  return useContext(ThemeContext);
}
